import { useAppSelector } from '@/store';

export type CustomerRateType = 'usd' | 'credits' | 'none';

interface ViewerForRateType {
  groups?: ReadonlyArray<{
    billing_account?: { isSMBAccount?: boolean | null } | null;
  } | null> | null;
  signup_type?: string | null;
}

/**
 * Centralized customer rate type detection.
 *
 * Priority:
 * 1. Has group with isSMBAccount=true -> 'usd'
 * 2. Has group with billing (not SMB) -> 'credits'
 * 3. signup_type === 'smb' (no billing yet, pending signup) -> 'usd'
 * 4. Otherwise -> 'none'
 */
export function getCustomerRateType(viewer: ViewerForRateType): CustomerRateType {
  const groups = viewer.groups || [];

  for (const group of groups) {
    if (group?.billing_account?.isSMBAccount === true) return 'usd';
  }

  for (const group of groups) {
    if (group?.billing_account) return 'credits';
  }

  if (viewer.signup_type === 'smb') return 'usd';

  return 'none';
}

/** React hook version of getCustomerRateType. */
export function useCustomerRateType(): CustomerRateType {
  const viewer = useAppSelector((state) => state.viewer);
  return getCustomerRateType(viewer);
}

/**
 * Rate type for a single, known billing account (e.g. an expert request's
 * account). This is the per-account counterpart to getCustomerRateType's
 * viewer scan — same `isSMBAccount -> usd, else credits` classification
 * contract (BillingClassification.tla). Use when the paying account is known
 * (an ER booking) rather than inferred from the viewer's memberships.
 */
export function rateTypeForBillingAccount(billingAccount: {
  isSMBAccount?: boolean | null;
}): 'usd' | 'credits' {
  return billingAccount?.isSMBAccount === true ? 'usd' : 'credits';
}
